import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Save, Trash2, Upload, Database, ChevronRight, MessageSquare, X, ToggleLeft, ToggleRight, DollarSign } from 'lucide-react';
import api from '../../utils/api';

/**
 * AnantFeelingsAdmin — Admin console for Anant Feelings AI companion personas.
 *
 * Panes:
 *  1. Per-persona edit — toggle active, edit tagline, gradient, tts_voice, system prompt, price, duration
 *  2. Recent sessions viewer
 *  3. Per-persona Knowledge Base upload (RAG)
 */
const AnantFeelingsAdmin = () => {
  const [personas, setPersonas] = useState([]);
  const [editing, setEditing] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [kbAvatarId, setKbAvatarId] = useState('');
  const [kbSources, setKbSources] = useState([]);
  const [kbFile, setKbFile] = useState(null);
  const [kbLabel, setKbLabel] = useState('');
  const [uploading, setUploading] = useState(false);

  const loadPersonas = () => api.get('/anant-feelings/admin/personas').then((r) => setPersonas(r.data.personas || [])).catch(() => {});
  const loadSessions = () => api.get('/anant-feelings/admin/sessions').then((r) => setSessions(r.data.sessions || [])).catch(() => {});
  const loadKB = (pid) => {
    if (!pid) return setKbSources([]);
    api.get(`/anant-feelings/admin/persona/${pid}/knowledge`).then((r) => setKbSources(r.data.sources || [])).catch(() => setKbSources([]));
  };

  useEffect(() => { loadPersonas(); loadSessions(); }, []);
  useEffect(() => { if (kbAvatarId) loadKB(kbAvatarId); }, [kbAvatarId]);

  const savePersona = async () => {
    if (!editing) return;
    try {
      const payload = { ...editing };
      delete payload.id; delete payload.created_at; delete payload.updated_at;
      // Coerce numeric fields
      if (payload.price_at != null && payload.price_at !== '') payload.price_at = parseFloat(payload.price_at);
      else delete payload.price_at;
      if (payload.duration_sec != null && payload.duration_sec !== '') payload.duration_sec = parseInt(payload.duration_sec, 10);
      else delete payload.duration_sec;
      if (payload.sort_order != null && payload.sort_order !== '') payload.sort_order = parseInt(payload.sort_order, 10);
      else delete payload.sort_order;
      await api.post(`/anant-feelings/admin/persona/${editing.id}`, payload);
      toast.success('Persona updated');
      setEditing(null);
      loadPersonas();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  const toggleActive = async (p) => {
    try {
      await api.post(`/anant-feelings/admin/persona/${p.id}`, { is_active: !p.is_active });
      loadPersonas();
    } catch { toast.error('Toggle failed'); }
  };

  const uploadKB = async () => {
    if (!kbAvatarId) return toast.error('Select a persona first');
    if (!kbFile) return toast.error('Choose a file');
    const fd = new FormData();
    fd.append('file', kbFile);
    if (kbLabel) fd.append('label', kbLabel);
    setUploading(true);
    try {
      const r = await api.post(`/anant-feelings/admin/persona/${kbAvatarId}/knowledge`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`Uploaded — ${r.data.chunks_added} chunks added`);
      setKbFile(null); setKbLabel('');
      loadKB(kbAvatarId);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally { setUploading(false); }
  };

  const deleteKBSource = async (source_file) => {
    if (!window.confirm(`Delete "${source_file}"?`)) return;
    try {
      await api.delete(`/anant-feelings/admin/persona/${kbAvatarId}/knowledge?source_file=${encodeURIComponent(source_file)}`);
      loadKB(kbAvatarId);
      toast.success('Deleted');
    } catch { toast.error('Delete failed'); }
  };

  return (
    <div className="max-w-5xl mx-auto py-8 px-4 space-y-6" data-testid="anant-feelings-admin">
      <div className="mb-2">
        <h1 className="text-3xl font-extrabold text-white">Anant Feelings — Admin</h1>
        <p className="text-slate-400 text-sm mt-1">Manage AI companion personas, pricing, prompts, and knowledge base.</p>
      </div>

      {/* Personas grid */}
      <div className="rounded-2xl bg-slate-900/70 border border-rose-400/30 p-5">
        <div className="text-rose-300 text-sm font-bold uppercase tracking-widest mb-4">Personas ({personas.length})</div>
        <div className="grid md:grid-cols-3 gap-4">
          {personas.map((p) => (
            <div key={p.id} className="rounded-xl bg-slate-800/60 border border-slate-700/60 overflow-hidden">
              <div className={`h-24 relative bg-gradient-to-br ${p.gradient || 'from-rose-500 to-purple-600'}`}>
                {p.avatar_url ? <img src={p.avatar_url} alt={p.name} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = 'none'; }} /> : null}
                <div className="absolute inset-0 flex items-center justify-center text-4xl font-black text-white/90 pointer-events-none">{p.name?.[0]}</div>
              </div>
              <div className="p-3 space-y-1.5">
                <div className="flex items-center justify-between">
                  <div className="font-bold text-white">{p.name}</div>
                  <button onClick={() => toggleActive(p)} data-testid={`toggle-active-${p.id}`}
                    className={`w-10 h-6 rounded-full flex items-center px-0.5 transition ${p.is_active ? 'bg-emerald-500 justify-end' : 'bg-slate-600 justify-start'}`}>
                    <div className="w-5 h-5 rounded-full bg-white" />
                  </button>
                </div>
                <div className="text-xs text-slate-400 truncate">{p.tagline}</div>
                <div className="flex items-center gap-2 text-[10px] text-amber-300 font-bold">
                  <DollarSign className="w-3 h-3" /> {p.price_at || 200} AT · {Math.floor((p.duration_sec || 600) / 60)} min
                </div>
                <button onClick={() => setEditing(p)} data-testid={`edit-persona-${p.id}`}
                  className="w-full mt-2 py-1.5 rounded bg-rose-500/20 text-rose-200 hover:bg-rose-500 hover:text-white text-xs font-bold">
                  Edit persona
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Edit modal */}
      {editing && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center px-4" data-testid="edit-persona-modal">
          <div className="max-w-lg w-full max-h-[90vh] rounded-2xl bg-slate-900 border-2 border-rose-400/40 flex flex-col">
            <div className="p-4 border-b border-slate-700 flex items-center justify-between">
              <div className="text-rose-300 font-bold">Edit {editing.name}</div>
              <button onClick={() => setEditing(null)} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {[
                ['name', 'Name', 'text'],
                ['tagline', 'Tagline', 'text'],
                ['avatar_url', 'Avatar URL', 'text'],
                ['video_loop_url', 'Video Loop URL', 'text'],
                ['gradient', 'Tailwind gradient classes', 'text'],
                ['tts_voice', 'TTS voice', 'text'],
                ['price_at', 'Price (AT)', 'number'],
                ['duration_sec', 'Duration (seconds)', 'number'],
                ['sort_order', 'Sort order', 'number'],
              ].map(([field, label, type]) => (
                <div key={field}>
                  <label className="text-xs text-slate-400 block mb-1">{label}</label>
                  <input type={type} value={editing[field] ?? ''}
                    onChange={(e) => setEditing({ ...editing, [field]: e.target.value })}
                    data-testid={`edit-${field}`}
                    className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white text-sm focus:border-rose-400 focus:outline-none" />
                </div>
              ))}
              <div>
                <label className="text-xs text-slate-400 block mb-1">System Prompt</label>
                <textarea value={editing.system_prompt || ''} rows={8}
                  onChange={(e) => setEditing({ ...editing, system_prompt: e.target.value })}
                  data-testid="edit-system-prompt"
                  className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white text-sm font-mono focus:border-rose-400 focus:outline-none" />
              </div>
            </div>
            <div className="p-4 border-t border-slate-700 flex gap-2">
              <button onClick={() => setEditing(null)} className="flex-1 py-2 rounded bg-slate-700 text-white text-sm">Cancel</button>
              <button onClick={savePersona} data-testid="save-persona" className="flex-1 py-2 rounded bg-emerald-500 text-black font-bold text-sm flex items-center justify-center gap-1">
                <Save className="w-4 h-4" /> Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Recent sessions */}
      <div className="rounded-2xl bg-slate-900/70 border border-fuchsia-400/30 p-5">
        <div className="text-fuchsia-300 text-sm font-bold uppercase tracking-widest mb-3 flex items-center gap-2">
          <MessageSquare className="w-4 h-4" /> Recent Sessions ({sessions.length})
        </div>
        <div className="max-h-72 overflow-y-auto space-y-2">
          {sessions.slice(0, 30).map((s) => (
            <div key={s.id} className="flex items-center gap-3 rounded-lg bg-slate-800/60 border border-slate-700/60 px-3 py-2 text-xs">
              <div className="flex-1 min-w-0">
                <div className="text-amber-200 font-bold truncate">{s.persona_id} · {s.user_id?.slice(0, 8)}…</div>
                <div className="text-slate-400 truncate">{s.status} · {s.started_at?.slice(0, 16)}</div>
              </div>
            </div>
          ))}
          {sessions.length === 0 && <div className="text-slate-500 text-sm text-center py-4">No sessions yet.</div>}
        </div>
      </div>

      {/* Per-persona KB */}
      <div className="rounded-2xl bg-slate-900/70 border border-emerald-400/30 p-5">
        <div className="text-emerald-300 text-sm font-bold uppercase tracking-widest mb-3 flex items-center gap-2">
          <Database className="w-4 h-4" /> Persona Knowledge Base (RAG)
        </div>
        <div className="text-slate-400 text-xs mb-3">
          Upload TXT/PDF/MD material per persona. Chunks are auto-injected into the AI system prompt on every user message so replies use your curated knowledge.
        </div>
        <div className="grid md:grid-cols-3 gap-3 mb-3">
          <select value={kbAvatarId} onChange={(e) => setKbAvatarId(e.target.value)} data-testid="kb-persona-select"
            className="px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white text-sm">
            <option value="">— Select Persona —</option>
            {personas.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <input type="file" accept=".txt,.md,.pdf,.csv,.json"
            onChange={(e) => setKbFile(e.target.files?.[0] || null)}
            data-testid="kb-file-input"
            className="px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white text-sm file:mr-2 file:px-3 file:py-1 file:rounded file:border-0 file:bg-emerald-500 file:text-black file:font-bold" />
          <input type="text" placeholder="Optional label" value={kbLabel}
            onChange={(e) => setKbLabel(e.target.value)}
            data-testid="kb-label-input"
            className="px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white text-sm" />
        </div>
        <button onClick={uploadKB} disabled={uploading || !kbAvatarId || !kbFile}
          data-testid="kb-upload-btn"
          className="px-4 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-black font-bold flex items-center gap-2 disabled:opacity-50">
          <Upload className="w-4 h-4" /> {uploading ? 'Uploading…' : 'Upload Knowledge'}
        </button>

        {kbAvatarId && (
          <div className="mt-4 space-y-2">
            {kbSources.map((s) => (
              <div key={s.source_file} className="rounded-lg bg-slate-800/60 border border-slate-700/60 p-3 text-xs flex items-start gap-2">
                <div className="flex-1 min-w-0">
                  <div className="text-emerald-200 font-bold truncate">{s.label || s.source_file}</div>
                  <div className="text-slate-500">{s.chunks} chunks · {s.source_file}</div>
                  {s.sample && <div className="text-slate-400 italic mt-1 line-clamp-2">{s.sample}</div>}
                </div>
                <button onClick={() => deleteKBSource(s.source_file)} className="w-8 h-8 rounded bg-rose-500/20 text-rose-300 hover:bg-rose-500 hover:text-white flex items-center justify-center">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
            {kbSources.length === 0 && <div className="text-slate-500 text-sm text-center py-3">No knowledge uploaded yet.</div>}
          </div>
        )}
      </div>
    </div>
  );
};

export default AnantFeelingsAdmin;
